import { useMemo, useState } from 'react';
import { AppButton } from '../Button/_index';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { MONTHS_ES, WEEKDAYS_ES, onBuildMonthGrid, onDateKey, onGroupByDay, onIsSameDay, onShiftMonth } from './_calendarGrid';

const BLOCK = 'LStudio__app_calendar';

/*
    🔥 CALENDARIO

    events: [{ id, date, title, tone, meta }]

    Vista de mes y vista de agenda. No es un selector de fecha: para
    eso ya tenés FieldDate. Esto MUESTRA lo que hay en cada día.

    ⚠️ MÁXIMO DE EVENTOS POR CELDA

    Con siete eventos en un día, la celda se estira y rompe la grilla
    del mes. Se muestran los primeros y el resto queda como "+3 más",
    que abre el día en la agenda.

    ⚠️ HOY SE MARCA CON BORDE, NO CON FONDO

    El fondo ya lo usa el día seleccionado. Si los dos fueran fondo, el
    día de hoy seleccionado no se distinguiría de cualquier otro.
*/

export const AppCalendar = ({
    events = [], value = null, // día seleccionado
    onSelectDay = null, onSelectEvent = null,
    view = 'month', // month | agenda
    onChangeView = null, weekStart = 1, // 1 lunes | 0 domingo
    maxPerDay = 3, weekdays = WEEKDAYS_ES, months = MONTHS_ES, emptyAgendaLabel = 'No hay eventos en este mes',
}) => {

    const MEMO__TODAY = useMemo(() => new Date(), []);

    const [useCursor, setCursor] = useState(() => {
        const BASE = value ?? MEMO__TODAY;
        return { year: BASE.getFullYear(), month: BASE.getMonth() };
    });

    const MEMO__GRID = useMemo(
        () => onBuildMonthGrid(useCursor.year, useCursor.month, weekStart),
        [useCursor, weekStart],
    );

    const MEMO__BY_DAY = useMemo(() => onGroupByDay(events), [events]);

    const MEMO__HEADS = useMemo(() => (
        weekStart === 1 ? weekdays : [weekdays[6], ...weekdays.slice(0, 6)]
    ), [weekdays, weekStart]);

    const MEMO__MONTH_EVENTS = useMemo(() => (
        MEMO__GRID.filter((cell) => !cell.isOutside).flatMap((cell) => (MEMO__BY_DAY[cell.key] ?? []).map((e) => ({ ...e, cell }))).sort((a, b) => a.date - b.date)
    ), [MEMO__GRID, MEMO__BY_DAY]);

    const onMove = (delta) => setCursor((prev) => onShiftMonth(prev.year, prev.month, delta));

    const onToday = () => setCursor({ year: MEMO__TODAY.getFullYear(), month: MEMO__TODAY.getMonth() });

    return (
        <div className={BLOCK} data-view={view}>

            {/* ==================== 🔥 CABECERA ==================== */}
            <header className={`${BLOCK}__head`}>

                <div className={`${BLOCK}__nav`}>

                    <AppButton variant="ghost" tone="neutral" size="sm" isIconOnly
                        icon={<FaChevronLeft />} aria-label="Mes anterior" onClick={() => onMove(-1)} />

                    <h3 className={`${BLOCK}__title`}>
                        {months[useCursor.month]} {useCursor.year}
                    </h3>

                    <AppButton variant="ghost" tone="neutral" size="sm" isIconOnly
                        icon={<FaChevronRight />} aria-label="Mes siguiente" onClick={() => onMove(1)} />

                </div>

                <div className={`${BLOCK}__tools`}>

                    <AppButton variant="outline" tone="neutral" size="sm" onClick={onToday}>
                        Hoy
                    </AppButton>

                    {
                        onChangeView && (
                            <div className={`${BLOCK}__views`}>

                                <AppButton size="sm" variant={view === 'month' ? 'solid' : 'ghost'}
                                    tone={view === 'month' ? 'accent' : 'neutral'}
                                    onClick={() => onChangeView('month')} >
                                    Mes
                                </AppButton>

                                <AppButton size="sm" variant={view === 'agenda' ? 'solid' : 'ghost'}
                                    tone={view === 'agenda' ? 'accent' : 'neutral'}
                                    onClick={() => onChangeView('agenda')} >
                                    Agenda
                                </AppButton>

                            </div>
                        )
                    }

                </div>

            </header>

            {/* ==================== 🔥 VISTA DE MES ==================== */}
            {
                view === 'month' && (
                    <>
                        <div className={`${BLOCK}__weekdays`} aria-hidden="true">
                            {MEMO__HEADS.map((d) => <span key={d} className={`${BLOCK}__weekday`}>{d}</span>)}
                        </div>

                        <div className={`${BLOCK}__grid`} role="grid">
                            {
                                MEMO__GRID.map((cell) => {

                                    const DAY_EVENTS = MEMO__BY_DAY[cell.key] ?? [];
                                    const EXTRA = DAY_EVENTS.length - maxPerDay;

                                    return (
                                        <div key={cell.key} className={`${BLOCK}__cell`}
                                            role="gridcell"  data-outside={cell.isOutside}  data-weekend={cell.isWeekend}
                                            data-today={onIsSameDay(cell.date, MEMO__TODAY)}  data-selected={!!value && onIsSameDay(cell.date, value)}
                                            tabIndex={0}  onClick={() => onSelectDay?.(cell.date, DAY_EVENTS)}
                                            onKeyDown={(e) => {
                                                if (e.key !== 'Enter' && e.key !== ' ') return;
                                                e.preventDefault();
                                                onSelectDay?.(cell.date, DAY_EVENTS);
                                            }} >

                                            <span className={`${BLOCK}__day`}>{cell.day}</span>

                                            <div className={`${BLOCK}__events`}>
                                                {
                                                    DAY_EVENTS.slice(0, maxPerDay).map((event) => (
                                                        <button   key={event.id ?? event.title}  type="button"
                                                            className={`${BLOCK}__event`}   data-tone={event.tone ?? 'accent'}
                                                            title={event.title}  onClick={(e) => { e.stopPropagation(); onSelectEvent?.(event); }} >
                                                            {event.title}
                                                        </button>
                                                    ))
                                                }

                                                {
                                                    EXTRA > 0 && (
                                                        <button type="button" className={`${BLOCK}__more`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onChangeView?.('agenda');
                                                                onSelectDay?.(cell.date, DAY_EVENTS);
                                                            }} >
                                                            +{EXTRA} más
                                                        </button>
                                                    )
                                                }
                                            </div>

                                        </div>
                                    );
                                })
                            }
                        </div>
                    </>
                )
            }

            {/* ==================== 🔥 VISTA DE AGENDA ==================== */}
            {
                view === 'agenda' && (
                    <div className={`${BLOCK}__agenda`}>
                        {
                            MEMO__MONTH_EVENTS.length === 0
                                ? <p className={`${BLOCK}__agenda_empty`}>{emptyAgendaLabel}</p>
                                : MEMO__MONTH_EVENTS.map((event) => (
                                    <button   key={event.id ?? `${event.title}_${onDateKey(event.date)}`}
                                        type="button" className={`${BLOCK}__agenda_row`}  data-tone={event.tone ?? 'accent'}
                                        onClick={() => onSelectEvent?.(event)} >

                                        <span className={`${BLOCK}__agenda_date`}>
                                            {String(event.date.getDate()).padStart(2, '0')}
                                            <em>{MEMO__HEADS[(event.date.getDay() - weekStart + 7) % 7]}</em>
                                        </span>

                                        <span className={`${BLOCK}__agenda_dot`} aria-hidden="true" />

                                        <span className={`${BLOCK}__agenda_text`}>

                                            <strong>{event.title}</strong>

                                            {event.meta && <em>{event.meta}</em>}

                                        </span>

                                    </button>
                                ))
                        }
                    </div>
                )
            }

        </div>
    );
};
